import React, { useEffect, useState } from 'react';
import { UiButton, UiSelect, UiTable } from '../../../components';
import './style.scss';
import { ACCESS_COLUMNS, ACCESS_LEVEL_KEY_MAPPING, ACCESS_LEVEL_VALUE_MAPPING } from './config';
import { Modal } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import * as api from '../../../actions';
import { ACCESSCONTROLMANAGEMENT, ADMINUSERMANAGEMENT } from '../../../apiServices/endpoints';
import { useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { loaderReducer } from '../../../reducer/loader.reducer';
import { showErrorNotification, showSuccessNotification } from '../../../components/uiNotification';

const AccessController = () => {
    const dispatch = useDispatch();
    const configResults = useSelector((state) => state?.configAction?.configData);
    const [menuItems, setMenuItems] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [adminUsers, setAdminUsers] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [existingAccessControlId, setExistingAccessControlId] = useState(null);
    const [isSavingAccessControls, setIsSavingAccessControls] = useState(false);

    const getAccessControlDetails = (role, userId) => api.get(`${ACCESSCONTROLMANAGEMENT}?role=${encodeURIComponent(role || '')}${userId ? `&userId=${encodeURIComponent(userId)}` : ''}`);
    const createAccessControl = (payload) => api.post(ACCESSCONTROLMANAGEMENT, payload);
    const updateAccessControl = (accessControlId, payload) => api.put(`${ACCESSCONTROLMANAGEMENT}/${accessControlId}`, payload);

    const buildPermissions = (menuDetails) => menuDetails?.map((menuDetail) => ({
        key: menuDetail?.key,
        menuName: menuDetail?.name,
        hide: false,
        view: false,
        add: false,
        f_update: false,
        p_update: false,
        delete: false,
    }));

    const buildAccessLevels = (permissionDetail) => Object.entries(ACCESS_LEVEL_KEY_MAPPING)
        ?.filter(([permissionKey]) => permissionDetail?.[permissionKey])
        ?.map(([, accessLevel]) => accessLevel);

    const buildAccessControlPayload = (selectedAdminUser, permissionDetails, roleValue) => {
        const selectedAdminUserId = selectedAdminUser?.adminUserId || '';
        return {
            ...(selectedAdminUserId ? { userId: selectedAdminUserId } : {}),
            role: selectedAdminUser?.role || roleValue || '',
            menus: permissionDetails?.map((permissionDetail) => ({
                menuName: permissionDetail?.menuName || '',
                accessLevels: buildAccessLevels(permissionDetail),
            })) || [],
        };
    };

    const buildPermissionsFromAccessControl = (menuDetails, accessControlMenus) => {
        const accessControlMenuMap = new Map(
            accessControlMenus?.map((menuDetail) => [
                menuDetail?.menuName,
                menuDetail?.accessLevels || [],
            ]) || []
        );
        return buildPermissions(menuDetails)?.map((permissionDetail) => {
            const accessLevels = accessControlMenuMap?.get(permissionDetail?.menuName) || [];
            return accessLevels?.reduce((updatedPermission, accessLevel) => {
                const permissionKey = ACCESS_LEVEL_VALUE_MAPPING?.[accessLevel];
                if (!permissionKey) {
                    return updatedPermission;
                }
                return {
                    ...updatedPermission,
                    [permissionKey]: true,
                };
            }, permissionDetail);
        }) || [];
    };

    const getAdminUserName = (adminUserDetail) => {
        const firstName = adminUserDetail?.firstName?.trim() || '';
        const middleName = adminUserDetail?.middleName?.trim() || '';
        const lastName = adminUserDetail?.lastName?.trim() || '';
        const fullName = [firstName, middleName, lastName]?.filter(Boolean)?.join(' ');
        return fullName || adminUserDetail?.email || adminUserDetail?.mobileNumber || 'Nil';
    };

    const mapAdminUser = (adminUserDetail) => ({
        adminUserId: adminUserDetail?.adminUserId || '',
        name: getAdminUserName(adminUserDetail),
        role: adminUserDetail?.role || 'Nil',
        email: adminUserDetail?.email || '',
        mobileNumber: adminUserDetail?.mobileNumber || '',
        userStatus: adminUserDetail?.userStatus || '',
    });

    const getAllAdminUsers = () => {
        const size = 10;
        const fetchAdminUsersByPage = (page, allAdminUsers) => api.get(`${ADMINUSERMANAGEMENT}/page?page=${page}&size=${size}`)
            .then((response) => {
                if (!(response?.statusCode === 200 || response?.status === 'OK')) {
                    return response;
                }
                const adminUserResponse = response?.result?.adminUserResponse;
                const updatedAdminUsers = [...allAdminUsers, ...(adminUserResponse?.content || [])];
                if (adminUserResponse?.last ?? true) {
                    return {
                        ...response,
                        result: {
                            ...response?.result,
                            adminUserResponse: {
                                ...response?.result?.adminUserResponse,
                                content: updatedAdminUsers,
                            },
                        },
                    };
                }
                return fetchAdminUsersByPage(page + 1, updatedAdminUsers);
            });
        return fetchAdminUsersByPage(0, []);
    };

    const { isFetching: isFetchingAdminUsers } = useQuery(
        ['FETCH_ACCESS_CONTROLLER_ADMIN_USERS'],
        getAllAdminUsers,
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.status === 'OK') {
                    const userList = response?.result?.adminUserResponse?.content?.map((adminUserDetail) => mapAdminUser(adminUserDetail)) || [];
                    setAdminUsers(userList);
                    return;
                }
                setAdminUsers([]);
                showErrorNotification('Error', response?.response?.data?.error?.message || response?.error?.message || response?.message || 'Failed to fetch admin users');
            },
            retry: false,
            refetchOnWindowFocus: false,
        }
    );

    const { isFetching: isFetchingAccessControls } = useQuery(
        ['FETCH_ACCESS_CONTROL_DETAILS', selectedRole, selectedUserId],
        () => getAccessControlDetails(selectedRole, selectedUserId),
        {
            enabled: Boolean(selectedRole),
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.status === 'OK') {
                    const accessControlResponse = response?.result?.accessControlResponse;
                    const accessControlDetail = Array.isArray(accessControlResponse)
                        ? accessControlResponse?.[0]
                        : accessControlResponse;
                    const accessControlMenus = Array.isArray(accessControlResponse)
                        ? accessControlResponse?.[0]?.menus || []
                        : accessControlResponse?.menus || [];
                    setExistingAccessControlId(accessControlDetail?.accessControlId || null);
                    setPermissions(buildPermissionsFromAccessControl(menuItems, accessControlMenus));
                    return;
                }
                setExistingAccessControlId(null);
                setPermissions(buildPermissions(menuItems));
                showErrorNotification('Error',response?.response?.data?.error?.message || response?.error?.message || response?.message || 'Failed to fetch access control details');
            },
            retry: false,
            refetchOnWindowFocus: false,
        }
    );

    const roleOptions = [...new Set(adminUsers?.map((adminUserDetail) => adminUserDetail?.role)?.filter(Boolean))]
        ?.map((roleValue) => ({
            key: roleValue,
            label: roleValue,
            value: roleValue,
        }));

    const filteredAdminUsers = !selectedRole
        ? adminUsers
        : adminUsers?.filter((adminUserDetail) => adminUserDetail?.role === selectedRole);

    const userOptions = filteredAdminUsers?.map((adminUserDetail) => ({
        key: adminUserDetail?.adminUserId,
        value: adminUserDetail?.adminUserId,
        label: adminUserDetail?.name,
        payload: adminUserDetail,
        searchText: [
            adminUserDetail?.name,
            adminUserDetail?.role,
            adminUserDetail?.email,
            adminUserDetail?.mobileNumber,
            adminUserDetail?.adminUserId,
        ]?.filter(Boolean)?.join(' ')?.toLowerCase(),
    }));

    const handlePermissionChange = (key, field, checked) => {
        const updatedPermissions = permissions?.map((permissionDetail) => {
            if (permissionDetail?.key === key) {
                const updatedPermissionDetail = { ...permissionDetail, [field]: checked };
                if (field === 'hide' && checked) {
                    return {
                        ...updatedPermissionDetail,
                        view: false,
                        add: false,
                        f_update: false,
                        p_update: false,
                        delete: false,
                    };
                }
                if (field === 'delete' && checked) {
                    return {
                        ...updatedPermissionDetail,
                        hide: false,
                        view: true,
                        add: true,
                        f_update: true,
                        p_update: true,
                    };
                }
                if (field !== 'hide' && checked) {
                    return {
                        ...updatedPermissionDetail,
                        hide: false,
                    };
                }
                return updatedPermissionDetail;
            }
            return permissionDetail;
        });
        setPermissions(updatedPermissions);
    };

    const handleRoleChange = (roleValue) => {
        const nextRole = roleValue || null;
        setSelectedRole(nextRole);
        setExistingAccessControlId(null);
        if (!nextRole || selectedUser?.role !== nextRole) {
            setSelectedUserId(null);
            setSelectedUser(null);
            setPermissions(buildPermissions(menuItems));
        }
    };

    const handleUserChange = (adminUserId, gstValue, selectedValue, selectedOption) => {
        const selectedAdminUser = selectedOption?.payload || null;
        setExistingAccessControlId(null);
        setSelectedUserId(adminUserId || null);
        setSelectedUser(selectedAdminUser);
        setSelectedRole(selectedAdminUser?.role || null);
    };

    const handleSaveClick = () => {
        if (!selectedRole) {
            showErrorNotification('Error', 'Please select a role to save access details');
            return;
        }
        setIsModalOpen(true);
    };

    const handleConfirmSave = () => {
        if (!selectedRole) {
            showErrorNotification('Error', 'Please select a role to save access details');
            return;
        }
        const accessControlPayload = buildAccessControlPayload(selectedUser, permissions, selectedRole);
        setIsSavingAccessControls(true);
        const saveAccessControlRequest = existingAccessControlId
            ? updateAccessControl(existingAccessControlId, accessControlPayload)
            : createAccessControl(accessControlPayload);
        saveAccessControlRequest
            ?.then((response) => {
                const responseData = response?.data || response;
                if (responseData?.statusCode === 201 || responseData?.statusCode === 200 || responseData?.status === 'OK'
                ) {
                    setIsModalOpen(false);
                    showSuccessNotification('Success', responseData?.result?.message || responseData?.result?.result || responseData?.message || 'Access control updated successfully');
                    return;
                }
                showErrorNotification('Error',response?.response?.data?.error?.message || response?.response?.data?.message || responseData?.error?.message || responseData?.message || response?.message || 'Failed to update access control');
            })
            ?.catch((error) => {
                showErrorNotification('Error',error?.response?.data?.error?.message || error?.response?.data?.message || error?.message || 'Failed to update access control');
            })
            ?.finally(() => {
                setIsSavingAccessControls(false);
            });
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    useEffect(() => {
        const configuredMenuItems = configResults
            ?.find((detail) => detail?.configId === 'AdminMenuItems')
            ?.configuration
            ?.filter((menuName) => typeof menuName === 'string' && menuName?.trim())
            ?.map((menuName, menuIndex) => ({ key: `${menuName?.trim()}_${menuIndex + 1}`, name: menuName?.trim() })) || [];
        setMenuItems(configuredMenuItems);
    }, [configResults]);

    useEffect(() => {
        if (!selectedRole && !selectedUserId) {
            setPermissions(buildPermissions(menuItems));
        }
    }, [menuItems, selectedRole, selectedUserId]);

    useEffect(() => {
        dispatch(loaderReducer(isFetchingAdminUsers || isFetchingAccessControls || isSavingAccessControls));
    }, [dispatch, isFetchingAccessControls, isFetchingAdminUsers, isSavingAccessControls]);

    return (
        <div className="access-controller-container">
            <div className="filter-section">
                <div className="filters">
                    <div className="filter-item">
                        <label>Select Role</label>
                        <UiSelect
                            placeholder="Select Role"
                            style={{ width: '100%' }}
                            value={selectedRole}
                            onChange={handleRoleChange}
                            options={roleOptions}
                        />
                    </div>
                    <div className="filter-item">
                        <label>Select User</label>
                        <UiSelect
                            placeholder={selectedRole ? 'Select User' : 'Select Role First'}
                            style={{ width: '100%' }}
                            value={selectedUserId}
                            onChange={handleUserChange}
                            options={selectedRole ? userOptions : []}
                            disabled={!selectedRole}
                            filterOption={(inputValue, option) => option?.searchText?.includes(inputValue?.toLowerCase())}
                        />
                        {selectedUser?.role && (
                            <div className="selected-role-info">
                                Selected Role: <span>{selectedUser?.role}</span>
                            </div>
                        )}
                    </div>
                </div>
                <div>
                     <UiButton 
                        add={false} 
                        className="save-btn" 
                        onClick={handleSaveClick}
                     >
                        Save Changes
                     </UiButton>
                </div>
            </div>

            <div className="permissions-table">
                <UiTable
                    columns={ACCESS_COLUMNS(handlePermissionChange)}
                    dataSource={permissions}
                    pagination={false}
                    rowKey="key"
                />
            </div>
            <Modal
                open={isModalOpen}
                footer={null}
                onCancel={handleCancel}
                centered
                width={500}
                className="access-confirm-modal"
            >
                <div className="confirm-modal-content">
                    <div className="icon-container">
                        <SyncOutlined style={{ fontSize: '32px', color: '#fff' }} />
                    </div>
                    <h3>Are You Sure, You Want To Update Your Changes ?</h3>
                    <div className="modal-actions">
                         <UiButton 
                            className="access-controller-cancel-button" 
                            onClick={handleCancel}
                         >
                            Cancel
                         </UiButton>
                         <UiButton 
                            className="access-controller-save-button" 
                            onClick={handleConfirmSave}
                         >
                            Save Changes
                         </UiButton>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AccessController;
